import React, { useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import type { AutocompleteItem, AutocompleteState } from './hooks/useChatAutocomplete';

interface AutocompleteDropdownProps {
  state: AutocompleteState;
  onSelect: (item: AutocompleteItem) => void;
  onClose: () => void;
}

/**
 * Floating dropdown for chat autocomplete suggestions.
 * Shows above the input when a trigger character (/, @, #) is detected.
 */
export const AutocompleteDropdown: React.FC<AutocompleteDropdownProps> = ({
  state,
  onSelect,
  onClose,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  // Scroll selected item into view
  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'nearest' });
  }, [state.selectedIndex]);

  // Close on outside click
  useEffect(() => {
    if (!state.isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (listRef.current && !listRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [state.isOpen, onClose]);

  if (!state.isOpen) return null;

  const triggerLabel =
    state.trigger === '/'
      ? 'Commands'
      : state.trigger === '@'
        ? 'People'
        : state.trigger === '#'
          ? 'Groups'
          : '';

  const emptyMessage =
    state.trigger === '@' && state.query.length < 3
      ? 'Type at least 3 characters to search…'
      : state.trigger === '@'
        ? 'No matching users found'
        : state.trigger === '#'
          ? 'No groups available'
          : 'No commands found';

  return (
    <div
      ref={listRef}
      role="listbox"
      aria-label={`${triggerLabel} suggestions`}
      className="absolute bottom-full left-0 right-0 mb-1 z-50 max-h-64 overflow-y-auto
        rounded-lg border border-border bg-popover text-popover-foreground shadow-lg"
    >
      {/* Header */}
      <div className="px-3 py-2 border-b border-border">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {triggerLabel}
        </span>
        {state.query && (
          <span className="ml-2 text-xs text-muted-foreground">
            — "{state.query}"
          </span>
        )}
      </div>

      {/* Loading state */}
      {state.isLoading && state.items.length === 0 && (
        <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>{emptyMessage}</span>
        </div>
      )}

      {/* Empty state */}
      {!state.isLoading && state.items.length === 0 && (
        <div className="px-3 py-3 text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      )}

      {/* Items */}
      {state.items.map((item, index) => {
        const isSelected = index === state.selectedIndex;

        return (
          <button
            key={item.id}
            ref={isSelected ? selectedRef : undefined}
            role="option"
            aria-selected={isSelected ? 'true' : 'false'}
            className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors
              hover:bg-accent hover:text-accent-foreground cursor-pointer
              ${isSelected ? 'bg-accent text-accent-foreground' : ''}`}
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent textarea blur
              onSelect(item);
            }}
            onMouseEnter={() => {
              // Optional: update selected index on hover for visual feedback
            }}
          >
            {/* Icon */}
            {item.icon && (
              <span className="flex-shrink-0 text-base w-6 text-center" aria-hidden="true">
                {item.icon}
              </span>
            )}

            {/* Text */}
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{item.label}</div>
              {item.description && (
                <div className="text-xs text-muted-foreground truncate">
                  {item.description}
                </div>
              )}
            </div>

            {/* Keyboard hint for selected item */}
            {isSelected && (
              <span className="flex-shrink-0 text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                Enter
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
